import React, { useRef, useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { LiveAudioVisualizer } from "react-audio-visualize";
import { ExclamationTriangleIcon } from "@heroicons/react/24/solid";
import StepIndicator from "../Components/StepIndicator";
import AppHeader from "../Components/AppHeader";
import { useErrorRenderer } from "../Hooks/useErrorRenderer";
import { showToast } from "../utils/toast";
import { useEventResult } from "../State/EventResultContext";
import { useLoginResult } from "../State/LoginResultContext";
import { useStreaming } from "../State/StreamingContext";
import StopStreamingModal from "../Components/StopStreamingModal";
import EndStreamingModal from "../Components/EndStreamingModal";
import { SUPPORTED_LANGUAGES } from "../utils/constants";
import CustomSelect from "../Components/CustomSelect";
import RealCaption from "../Components/RealCaption";

function SessionStatusIndicator({ status }) {
  const amberSpinner = (
    <span
      className="inline-flex h-4 w-4 shrink-0 items-center justify-center"
      aria-hidden
    >
      <span className="h-3 w-3 rounded-full border-2 border-amber-400 border-t-transparent animate-spin" />
    </span>
  );
  const greenDot = (
    <span
      className="h-2.5 w-2.5 shrink-0 rounded-full bg-[#22c55e] shadow-[0_0_6px_rgba(34,197,94,0.6)]"
      aria-hidden
    />
  );
  const redWarning = (
    <ExclamationTriangleIcon
      className="h-5 w-5 shrink-0 text-red-500"
      aria-hidden
    />
  );
  const mutedDot = (
    <span className="h-2.5 w-2.5 shrink-0 rounded-full bg-white/35" aria-hidden />
  );

  let icon;
  let textClass = "text-white/90";
  if (status === "Getting ready…" || status === "Reconnecting…") {
    icon = amberSpinner;
  } else if (status === "Captions Live") {
    icon = greenDot;
  } else if (status === "Connection Lost") {
    icon = redWarning;
  } else {
    icon = mutedDot;
  }

  return (
    <span className="flex items-center gap-2">
      {icon}
      <span className={`font-semibold atkinson-font text-[20px] ${textClass}`}>
        {status}
      </span>
    </span>
  );
}

export default function CaptionsPreview() {
  const navigate = useNavigate();
  const { eventResults } = useEventResult();
  const { loginResults } = useLoginResult();
  const [message, setMessage, isError, setIsError] = useErrorRenderer();

  const { captions, speechLanguage, durationText, numConnected, status, streamMediaRecorder, 
          stop, setSpeechLang, lastTranscriptArrived, justReconnected } = useStreaming();

  const captionsContainerRef = useRef(null);

  const [ endStream, setEndStream ] = useState(false);
  const [stopConfirm, setStopConfirm] = useState(false);
  const [stopBusy, setStopBusy] = useState(false);

  const supportedLangCodes = Object.keys(SUPPORTED_LANGUAGES)

  const textColor = "white";
  const fontSize = "16";
  const backgroundColor = "black";

    const runEndStream = () => {
    try {
          stop({
            eventId: eventResults?.id,
            organizationId: loginResults?.id,
          });
        } catch (e) {
          setIsError(true);
          setMessage(e?.message || "Failed to stop");
        }
        navigate("/my-events");
  }

  const triggerEndStream = useCallback(() => {
    runEndStream();
    setTimeout(() => {
      setEndStream(true);
    }, 2000);
  }, [runEndStream]);

  
    const openStopConfirm = useCallback((event) => {
      setStopConfirm(true);
    }, []);
  
    const closeStopConfirm = useCallback(() => {
      if (!stopBusy) setStopConfirm(false);
    }, [stopBusy]);
  
    const confirmStop = useCallback(async () => {
      if (!stopConfirm) return;
      setStopBusy(true);
      try {
        await Promise.resolve(runEndStream());
      } finally {
        setStopBusy(false);
        setStopConfirm(false);
      }
    }, [stopConfirm, runEndStream]);



  useEffect(() => {
    if (captionsContainerRef.current) {
      captionsContainerRef.current.scrollTop =
        captionsContainerRef.current.scrollHeight;
    }
  }, [captions]);
  

  useEffect(() => {
    if (lastTranscriptArrived) {
      const interval = setInterval(() => {
        const now = new Date().getTime();
        const timeSinceLastTranscript = now - lastTranscriptArrived;
        if (timeSinceLastTranscript > 45 * 60 * 1000) { // 45 minutes
          triggerEndStream();
          clearInterval(interval); // stop checking once triggered
        }
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [lastTranscriptArrived, triggerEndStream]);


  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(loginResults?.permalink);
      showToast("Link copied to clipboard!", { type: "success" });
    } catch (e) {
      setIsError(true);
      setMessage(e?.message || "Copy failed");
    }
  };



  return (
    <div className="min-h-full flex-col">
      <AppHeader className="sm:hidden" isSuperUser={loginResults?.isSuperUser} loginResults={loginResults} />

      <div className="flex flex-1 flex-col items-center px-6 py-2 sm:py-10">
        <div className="flex flex-col items-center">
          <img
            src="logo192.png"
            alt="Seymour Logo"
            className="mx-auto h-11 sm:h-20 w-auto rounded-full hidden sm:block"
          />
          <StepIndicator currentStep={3} className="sm:mt-6" />
        </div>

        <div className="sm:grid sm:grid-cols-12 gap-8 w-full sm:max-w-7xl mt-6 sm:mt-10">
          <div className="col-span-3"></div>
          {/* Main column */}
          <div className="flex-1 flex flex-col items-center sm:col-span-6">
            <h2 className="text-center text-base sm:text-[40px] font-extrabold text-white atkinson-font tracking-tight">
              {eventResults?.name}
            </h2>

            <div className="w-full max-w-xl mt-8 sm:mt-2">
              <p className="text-white text-[20px] font-bold atkinson-font mb-2">
                Speech Language
              </p>
               <div className="grid grid-cols-1 gap-4 mb-4 ">
                  <CustomSelect
                    value={speechLanguage}
                    options={supportedLangCodes.map((code) => ({
                      value: code,
                      label: SUPPORTED_LANGUAGES[code],
                    }))}
                    onChange={(val) => setSpeechLang({speechLanguage: val})}
                  />
                </div>

              <p className="text-white text-[20px] font-bold atkinson-font mb-2">
                Captions Preview
              </p>

              <div
                ref={captionsContainerRef}
                className="bg-[#000000] rounded-xl atkinson-font border-2 border-[#5701AE] px-3 py-2 sm:px-5 sm:py-4 text-white text-[18px] leading-relaxed h-[150px] sm:h-[200px] overflow-y-auto"
                style={{ boxShadow: "0 0 0 1px rgba(255,255,255,0.08)" }}
              >
                <RealCaption
                  showSetting={false}
                  captions={captions}
                  justReconnected={justReconnected}
                  textColor={textColor}
                  backgroundColor={backgroundColor}
                  fontSize={fontSize}
                />
               
              </div>

              <div className="flex items-center justify-center gap-3 mt-3">
                <p className="text-white font-semibold atkinson-font m-0">
                  Mic On:
                </p>
                {streamMediaRecorder ? (
                  <LiveAudioVisualizer
                    mediaRecorder={streamMediaRecorder}
                    width={30}
                    height={20}
                    // barColor="#7A2FD3"
                    barColor="white"
                    gap={2}
                    barWidth={2}
                  />
                ) : (
                  <div className="h-[22px] w-[80px]" />
                )}
              </div>

              <div className="mt-8 w-full max-w-xl space-y-4">
                <div className="flex items-center justify-between gap-3 text-white">
                  <span className="font-semibold atkinson-font text-[20px]">Status:</span>
                  <SessionStatusIndicator status={status} />
                </div>
                <div className="flex items-center justify-between text-white">
                  <span className="font-semibold atkinson-font text-[20px]">Duration:</span>
                  <span className="font-mono text-[20px]">{durationText}</span>
                </div>
                <div className="flex items-center justify-between text-white">
                  <span className="font-semibold atkinson-font text-[20px]">
                    Connected Users:
                  </span>
                  <span className="font-mono text-[20px]" id="numConnected">
                    {numConnected ?? 0}
                  </span>
                </div>
              </div>

              {/* Mobile share link */}
              <div className="sm:hidden mt-8 ">
                <h3 className="text-white font-bold atkinson-font text-[20px]">
                  Share your link
                </h3>
                <p className="text-white/80 text-sm atkinson-font m-0 sm:mt-1">
                  This link works for all your live streams.
                </p>
                <div className="bg-[#00000070] relative mt-3 p-3 text-white flex items-center gap-3 rounded-lg border border-white ">
                  {loginResults?.permalink?.slice(0, 35)}...
                  <button
                    type="button"
                    style={{
                      border: "2px solid #6A6680",
                    }}
                    className="absolute w-fit right-0 bg-[#5701AE] text-white text-[13px] rounded-lg py-1 px-3 font-semibold shadow-[0_0_0_1px_rgba(255,255,255,0.2)] hover:bg-[#7c4dff] transition-colors"
                    onClick={copyLink}
                  >
                    Copy
                  </button>

                

                </div>                 

              </div>

              <button
                type="button"
                onClick={() => {
                  openStopConfirm();
                }}
                // className="mt-8 w-full max-w-xl flex justify-center rounded-lg !bg-[#D32F2F] p-3 text-base font-semibold text-white hover:!bg-[#C62828]"
                className="mt-12 flex w-full justify-center rounded-md !bg-[#D32F2F] p-2 text-base font-semibold text-white hover:!bg-[#C62828]"
                style={{ border: "2px solid #6A6680" }}
              >
                Stop Speech to Text
              </button>
            </div>
          </div>

          {/* Desktop share link card */}
          <div className="hidden sm:block w-[330px] sm:col-span-3">
            <div className="bg-[#00000070] rounded-[18px] p-6 border border-[#4a148c]">
              <h3 className="text-white font-bold atkinson-font text-[28px] mb-3">
                Share your link
              </h3>
              <div className="border border-white rounded-lg px-3 py-2 text-white text-sm break-all">
                {loginResults?.permalink?.slice(0, 35)}...
              </div>
              <button
                type="button"
                className="mt-3 w-full bg-[#5701AE] text-white rounded-lg px-4 py-2 font-semibold"
                style={{ border: "2px solid #6A6680" }}
                onClick={copyLink}
              >
                Copy
              </button>
            </div>
          </div>
        </div>
      </div>

      
      <StopStreamingModal
              open={stopConfirm}
              onClose={closeStopConfirm}
              onConfirm={confirmStop}
              busy={stopBusy}
            />

      <EndStreamingModal open={endStream} />

    </div>
  );
}
